/**
 * Coordinating hook for AssetDrawer data fetching
 *
 * Consolidates queries (assets, attributes, risks, technologiesTree)
 * into a single interface while maintaining React Query's automatic parallelism.
 *
 * OSINT partition is handled automatically via OsintContext — when the drawer
 * is wrapped in OsintProvider, all useMy calls use the OSINT partition.
 */

import { useMemo } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { useGetUserKey } from '@/hooks/useQueryKeys';
import {
  Asset,
  Attribute,
  RiskSeverity,
  TreeNode,
  UniqueQueryKeys,
} from '@/types';
import { useQuery } from '@/utils/api';

type QueryStatus = 'pending' | 'error' | 'success';

export interface UseAssetDrawerDataResult {
  /** Main asset data array (typically single asset) */
  assets: Asset[];
  /** Asset attributes from graph query */
  assetAttributes: Attribute[];
  /** Per-asset OPEN risk counts by severity (e.g., { C: 2, H: 5 }) */
  riskCounts: Partial<Record<RiskSeverity, number>> | undefined;
  /** Technologies tree structure from graph query */
  technologiesTree: TreeNode[];
  /** Combined asset object with attributes attached */
  asset: Asset;
  /** Entity group extracted from first asset */
  entityGroup: string;
  /** Entity identifier extracted from first asset */
  entityIdentifier: string;
  /** True if any query is in initial loading state */
  isInitialLoading: boolean;
  /** True if any query is loading (including refetches) */
  isLoading: boolean;
  /** Individual query statuses for granular control */
  statuses: {
    assets: QueryStatus;
    attributes: QueryStatus;
    riskCounts: QueryStatus;
    technologies: QueryStatus;
  };
}

/**
 * Custom hook for coordinating AssetDrawer data queries
 *
 * Executes 4 queries in parallel (all need assetKey):
 * assets, attributes, risks (for per-asset vuln counts), technologies
 *
 * @param assetKey - The asset key to fetch data for
 * @returns Consolidated data with loading states
 */
export function useAssetDrawerData(assetKey: string): UseAssetDrawerDataResult {
  // Query 1: Fetch main asset data
  const { data: assets = [], status: assetsStatus } = useMy(
    {
      resource: 'asset',
      query: {
        node: {
          labels: ['Asset'],
          filters: [
            { field: 'key', operator: '=', value: assetKey || '', not: false },
          ],
        },
      },
    },
    { enabled: Boolean(assetKey) }
  );

  // Query 2: Fetch asset attributes (parallel with assets)
  const { data: assetAttributes = [], status: attributesStatus } = useMy(
    {
      query: {
        node: {
          labels: ['Attribute'],
          relationships: [
            {
              label: 'HAS_ATTRIBUTE',
              source: {
                labels: ['Asset'],
                filters: [
                  { field: 'key', operator: '=', value: assetKey || '' },
                ],
              },
            },
          ],
        },
      },
      resource: 'attribute',
    },
    { enabled: Boolean(assetKey) }
  );

  // Extract entityGroup and entityIdentifier from assets
  const entityGroup = assets[0] ? assets[0].group : '';
  const entityIdentifier = assets[0] ? assets[0].identifier : '';

  // Query 3: Fetch per-asset OPEN risk counts grouped by status via groupBy
  const axios = useAxios({});
  const riskCountsQuery = useQuery<{
    groups: { fields: Record<string, string>; count: number }[];
  }>({
    defaultErrorMessage: 'Failed to fetch risk counts',
    queryKey: useGetUserKey(
      [UniqueQueryKeys.MY, 'risk-counts-grouped', assetKey],
      false
    ),
    queryFn: async () => {
      const { data } = await axios({
        method: 'POST',
        url: '/my',
        params: { label: 'risk' },
        data: {
          node: {
            labels: ['Risk'],
            // Open-only ('O') by design: this widget is the "Open Vulnerabilities"
            // count, and it matches the drawer list's own default status filter
            // (useRisks defaultFilters => status: ['O']). Do NOT broaden to all
            // statuses — the severity here is derived from status.charAt(1), and
            // no-severity statuses (Deleted, RiskStatusWithoutSeverity) would
            // bucket under invalid/Info keys. The count is intentionally scoped
            // to Open and labelled as such so a non-Open asset reading 0 is
            // truthful, not a list mismatch. (ENG-4524)
            filters: [{ field: 'status', operator: 'STARTS WITH', value: 'O' }],
            relationships: [
              {
                label: ['HAS_VULNERABILITY', 'HAS_PORT', 'HAS_WEBPAGE'],
                length: 3,
                source: {
                  labels: ['Asset'],
                  filters: [{ field: 'key', operator: '=', value: assetKey }],
                },
              },
            ],
          },
          groupBy: ['status'],
          limit: 1000,
        },
      });
      return data;
    },
    enabled: Boolean(assetKey),
  });

  const riskCounts = useMemo(() => {
    if (!riskCountsQuery.data?.groups) return undefined;
    const counts: Partial<Record<RiskSeverity, number>> = {};
    for (const group of riskCountsQuery.data.groups) {
      const status = group.fields?.status ?? '';
      const severity = (status.charAt(1) || 'I') as RiskSeverity;
      counts[severity] = (counts[severity] ?? 0) + group.count;
    }
    return counts;
  }, [riskCountsQuery.data]);

  const riskCountsStatus = riskCountsQuery.status;

  // Query 4: Fetch technologies tree (parallel with assets and attributes)
  const { data: technologiesTree = [], status: technologiesStatus } = useMy(
    {
      tree: true,
      query: {
        node: {
          labels: ['Asset'],
          filters: [
            {
              field: 'key',
              operator: '=',
              value: assetKey || '',
            },
          ],
          relationships: [
            {
              label: 'HAS_PORT',
              target: {
                labels: ['Port'],
                relationships: [
                  {
                    label: 'HAS_TECHNOLOGY',
                    optional: true,
                    target: { labels: ['Technology'] },
                  },
                ],
              },
            },
          ],
        },
      },
      resource: 'technology',
    },
    { enabled: Boolean(assetKey) }
  );

  // Combine asset with attributes for components that need it
  // Note: Using full array dependencies as React Compiler prefers simpler inference
  const asset: Asset = useMemo(() => {
    const baseAsset = assets[0] || ({} as Asset);
    return {
      ...baseAsset,
      attributes: assetAttributes,
    };
  }, [assets, assetAttributes]);

  // Calculate unified loading states
  const isInitialLoading =
    assetsStatus === 'pending' ||
    attributesStatus === 'pending' ||
    riskCountsStatus === 'pending';

  const isLoading =
    assetsStatus === 'pending' ||
    attributesStatus === 'pending' ||
    riskCountsStatus === 'pending' ||
    technologiesStatus === 'pending';

  return {
    assets,
    assetAttributes,
    riskCounts,
    technologiesTree,
    asset,
    entityGroup,
    entityIdentifier,
    isInitialLoading,
    isLoading,
    statuses: {
      assets: assetsStatus,
      attributes: attributesStatus,
      riskCounts: riskCountsStatus,
      technologies: technologiesStatus,
    },
  };
}
